import { api } from '@/lib/api';

export type LoyaltyActivityType =
  | 'OPD_VISIT'
  | 'IPD_ADMISSION'
  | 'PHARMACY'
  | 'LAB'
  | 'HEALTH_CHECKUP'
  | 'REFERRAL'
  | 'APP_ENGAGEMENT'
  | 'VACCINATION'
  | 'MANUAL';

export type LoyaltyBurnPurpose =
  | 'OPD_DISCOUNT'
  | 'PHARMACY_DISCOUNT'
  | 'FREE_CONSULTATION'
  | 'DIAGNOSTICS'
  | 'MEMBERSHIP_UPGRADE'
  | 'IPD_DISCOUNT'
  | 'DAY_CARE_DISCOUNT';

export const LOYALTY_REDEEM_HOLD_MESSAGE =
  'Points can be redeemed 24 hours after they are credited to your account.';

export interface LoyaltySettings {
  loyaltyEnabled: boolean;
  defaultOpdVisitPoints: number;
  defaultIpdPoints: number;
  defaultReferralPoints: number;
  defaultPharmacyPercentOfSpend?: number;
  defaultHealthCheckupPoints?: number;
  defaultVaccinationPoints?: number;
  defaultAppEngagementPoints?: number;
  defaultLabPercentOfSpend?: number;
  defaultLabFixedPoints?: number;
  pointExpiryMonths: number | null;
  maxRedemptionPercent: number;
  pointsPerRupee: number;
  appDisplayName: string | null;
  primaryColor: string | null;
  logoUrl: string | null;
  familyPointsPoolingEnabled?: boolean;
  referralPromoCreditReferrer?: number;
  referralPromoCreditReferred?: number;
}

export interface LoyaltyEarnRule {
  id: string;
  activityType: LoyaltyActivityType;
  points: number;
  percentOfSpend: number;
  isActive: boolean;
  description: string | null;
}

export interface LoyaltyBurnRule {
  id: string;
  purpose: LoyaltyBurnPurpose;
  pointsCost: number | null;
  maxRedemptionPercent: number | null;
  minPointsToRedeem: number;
  isActive: boolean;
  description: string | null;
}

export interface LoyaltyTransactionRow {
  id: string;
  type: string;
  activityType: string;
  burnPurpose?: string | null;
  points: number;
  description: string | null;
  createdAt: string;
}

export interface PatientLoyaltySummary {
  loyaltyEnabled: boolean;
  pointsBalance: number;
  redeemableBalance?: number;
  pointsOnHold?: number;
  redeemHoldHours?: number;
  redeemHoldMessage?: string;
  nextRedeemableAt?: string | null;
  ownPointsBalance?: number;
  familyPointsBalance?: number;
  familyPoolingEnabled?: boolean;
  familyMembers?: Array<{ id: string; name: string }>;
  rupeeValue: number;
  pointsPerRupee?: number;
  maxRedemptionPercent?: number;
  cashbackBalance: number;
  promoBalance: number;
  refundBalance: number;
  membership: { level: string; displayName: string } | null;
  earnRules?: LoyaltyEarnRule[];
  burnRules?: LoyaltyBurnRule[];
  recentTransactions: LoyaltyTransactionRow[];
}

export interface LoyaltyAnalytics {
  wallets: number;
  pointsLiability: number;
  cashbackLiability: number;
  promoLiability: number;
  activeMembersWithPoints: number;
  earnPointsLast30d: number;
  earnTransactionsLast30d: number;
  burnPointsLast30d: number;
  burnTransactionsLast30d: number;
  referrals: Array<{ status: string; count: number }>;
  referralConversionPct: number;
  repeatVisitRatePct: number;
  tierDistribution: Array<{ tier: string; level?: string; count: number }>;
  challengeCompletions: number;
  totalActivePatients: number;
}

export const EARN_RULE_LABELS: Record<LoyaltyActivityType, string> = {
  OPD_VISIT: 'OPD visit',
  IPD_ADMISSION: 'IPD admission',
  PHARMACY: 'Pharmacy purchase',
  LAB: 'Lab / diagnostics',
  HEALTH_CHECKUP: 'Health checkup',
  REFERRAL: 'Referral',
  APP_ENGAGEMENT: 'App engagement',
  VACCINATION: 'Vaccination',
  MANUAL: 'Manual',
};

export const BURN_RULE_LABELS: Record<LoyaltyBurnPurpose, string> = {
  OPD_DISCOUNT: 'OPD discount',
  PHARMACY_DISCOUNT: 'Pharmacy discount',
  FREE_CONSULTATION: 'Free consultation',
  DIAGNOSTICS: 'Diagnostics discount',
  MEMBERSHIP_UPGRADE: 'Membership upgrade',
  IPD_DISCOUNT: 'IPD discount',
  DAY_CARE_DISCOUNT: 'Day Care discount',
};

/** Staff who can redeem points on behalf of a patient during billing (POS). */
export const LOYALTY_POS_STAFF_ROLES = [
  'SUPER_ADMIN',
  'HOSPITAL_ADMIN',
  'RECEPTIONIST',
  'PHARMACIST',
] as const;

export function canRedeemLoyaltyAtPos(role?: string | null): boolean {
  return !!role && (LOYALTY_POS_STAFF_ROLES as readonly string[]).includes(role);
}

/** Match bill line types to the correct loyalty burn rule (OPD / pharmacy / lab). */
export function inferLoyaltyBurnPurposeFromBillItems(
  items: Array<{ itemType?: string }> | undefined
): LoyaltyBurnPurpose {
  const list = items ?? [];
  if (list.some((i) => i.itemType === 'MEDICINE')) return 'PHARMACY_DISCOUNT';
  if (list.some((i) => i.itemType === 'LAB_TEST')) return 'DIAGNOSTICS';
  return 'OPD_DISCOUNT';
}

export type PatientPortalAnnouncementType = 'DISCOUNT' | 'HEALTH_CAMP' | 'PROMO' | 'GENERAL';

export interface PatientPortalAnnouncement {
  id: string;
  message: string;
  type: PatientPortalAnnouncementType;
  linkUrl?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string;
}

export const ANNOUNCEMENT_TYPE_LABELS: Record<PatientPortalAnnouncementType, string> = {
  DISCOUNT: 'Discount / offer',
  HEALTH_CAMP: 'Health camp',
  PROMO: 'Promotion',
  GENERAL: 'General update',
};

export const loyaltyApi = {
  getSettings: () =>
    api.get('/api/loyalty/settings').then(
      (r) =>
        r.data.data as {
          settings: LoyaltySettings;
          rules: LoyaltyEarnRule[];
          burnRules: LoyaltyBurnRule[];
          tiers: Array<{
            id: string;
            level: string;
            displayName: string;
            minAnnualSpend: number;
            minVisitsPerYear: number;
            checkupDiscountPct: number;
            priorityAppointments: boolean;
          }>;
        }
    ),

  updateSettings: (payload: Partial<LoyaltySettings>) =>
    api.put('/api/loyalty/settings', payload).then((r) => r.data.data),

  updateRules: (payload: {
    earnRules?: Array<Partial<LoyaltyEarnRule> & { activityType: LoyaltyActivityType }>;
    burnRules?: Array<Partial<LoyaltyBurnRule> & { purpose: LoyaltyBurnPurpose }>;
  }) => api.put('/api/loyalty/rules', payload).then((r) => r.data.data),

  getPatientSummary: (patientId: string) =>
    api.get(`/api/loyalty/patient/${patientId}`).then((r) => r.data.data as PatientLoyaltySummary),

  getRedemptionQuote: (
    patientId: string,
    purpose: LoyaltyBurnPurpose,
    billAmount?: number
  ) =>
    api
      .get(`/api/loyalty/patient/${patientId}/redemption-quote`, {
        params: { purpose, billAmount },
      })
      .then(
        (r) =>
          r.data.data as {
            maxPoints: number;
            maxRupees: number;
            pointsCost: number | null;
            redeemableBalance?: number;
            pointsOnHold?: number;
            redeemHoldMessage?: string;
            nextRedeemableAt?: string | null;
          }
      ),

  redeemPoints: (
    patientId: string,
    points: number,
    options?: { purpose?: LoyaltyBurnPurpose; billId?: string; billAmount?: number; notes?: string }
  ) =>
    api
      .post(`/api/loyalty/patient/${patientId}/redeem`, {
        points,
        purpose: options?.purpose,
        billId: options?.billId,
        billAmount: options?.billAmount,
        notes: options?.notes,
      })
      .then(
        (r) =>
          r.data.data as {
            pointsBurned: number;
            rupeeValue: number;
            membershipTierName?: string;
          }
      ),

  creditExtraPoints: (patientId: string, points: number, description?: string) =>
    api
      .post(`/api/loyalty/patient/${patientId}/credit`, { points, description })
      .then((r) => r.data.data as { transactionId: string; points: number; balanceAfter: number }),

  getAnalytics: () => api.get('/api/loyalty/analytics').then((r) => r.data.data as LoyaltyAnalytics),

  listCampaigns: () => api.get('/api/loyalty/campaigns').then((r) => r.data.data),

  createCampaign: (payload: Record<string, unknown>) =>
    api.post('/api/loyalty/campaigns', payload).then((r) => r.data.data),

  broadcastCampaigns: () =>
    api.post('/api/loyalty/campaigns/broadcast').then((r) => r.data.data),

  listAnnouncements: () =>
    api.get('/api/loyalty/announcements').then((r) => r.data.data as PatientPortalAnnouncement[]),

  createAnnouncement: (payload: {
    message: string;
    type?: PatientPortalAnnouncementType;
    linkUrl?: string;
    sortOrder?: number;
    isActive?: boolean;
    startsAt?: string;
    endsAt?: string;
  }) => api.post('/api/loyalty/announcements', payload).then((r) => r.data.data),

  updateAnnouncement: (
    id: string,
    payload: Partial<{
      message: string;
      type: PatientPortalAnnouncementType;
      linkUrl: string | null;
      sortOrder: number;
      isActive: boolean;
      startsAt: string | null;
      endsAt: string | null;
    }>
  ) => api.put(`/api/loyalty/announcements/${id}`, payload).then((r) => r.data.data),

  deleteAnnouncement: (id: string) =>
    api.delete(`/api/loyalty/announcements/${id}`).then((r) => r.data.data),

  listChallenges: () => api.get('/api/loyalty/challenges').then((r) => r.data.data),

  createChallenge: (payload: Record<string, unknown>) =>
    api.post('/api/loyalty/challenges', payload).then((r) => r.data.data),

  updateTier: (tierId: string, payload: Record<string, unknown>) =>
    api.put(`/api/loyalty/tiers/${tierId}`, payload).then((r) => r.data.data),

  runMembershipJob: () => api.post('/api/loyalty/membership/run').then((r) => r.data.data),
};
